// components
import { ControlledTextField, Dialog } from 'components/shared';

// external libraries
import { useTranslation } from 'react-i18next';
import useGetFlexMatchAskEmployeeMessage from '../hooks/useGetFlexMatchAskEmployeeMessage';
import { Skeleton } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import useSendTaskMessageOutbound from '../hooks/useSendTaskMessageOutbound';
import { useEffect } from 'react';
import { ControlledRichTextEditor } from 'components/shared/RichTextEditor/ControlledRichTextEditor';
import { useQueryClient } from 'react-query';
import { StyledTextFieldContainer } from './CommunicationDialog.styles';
import { StyledCustomDialogPaperSmall } from './form/QualificationSection.styles';

const CommunicationDialog = ({
  isOpen,
  onClose,
  selectedEmployee,
  selectedRow,
  isPhoneMessage
}) => {
  'use memo';

  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { data: askEmployeeMessage, isLoading } =
    useGetFlexMatchAskEmployeeMessage(
      {
        workAgreementId: selectedEmployee?.workAgreementId,
        processInstanceId: +selectedRow?.processInstanceId,
        employeeId: selectedEmployee?.employeeId,
        messageType: isPhoneMessage ? 'phone' : undefined
      },
      {
        enabled: !!isOpen && !!selectedEmployee && !!selectedRow
      }
    );

  const { mutateAsync: sendTaskMessageOutbound } = useSendTaskMessageOutbound();

  // remove html tags from subject
  const subject = askEmployeeMessage
    ? DOMPurify.sanitize(askEmployeeMessage.subject, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: []
      })
    : '';

  const methods = useForm({
    defaultValues: {
      subject: subject || '',
      body: askEmployeeMessage?.body || ''
    }
  });

  const { control, setValue } = methods;

  const handleSendMessage = () => {
    const { subject, body } = methods.getValues();
    sendTaskMessageOutbound(
      {
        askedTaskId: selectedEmployee?.askedTaskId,
        body: {
          type: isPhoneMessage ? 'PHONE' : 'EMAIL',
          message: {
            subject: isPhoneMessage ? undefined : subject,
            body
          }
        }
      },
      {
        onSuccess: () => {
          handleClose();
          queryClient.invalidateQueries([
            'flexMatchCandidates',
            selectedRow?.requestId
          ]);
          queryClient.invalidateQueries([
            'flexMatchEmployees',
            selectedRow?.requestId
          ]);
        }
      }
    );
  };

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    setValue('subject', subject || '');
  }, [subject, setValue]);

  useEffect(() => {
    if (askEmployeeMessage?.body != null) {
      setValue('body', askEmployeeMessage.body);
    }
  }, [askEmployeeMessage?.body, setValue]);

  if (!selectedEmployee || !isOpen || !selectedRow) return null;

  if (isLoading) return <Skeleton variant="rounded" height={400} />;

  const isPhoneType = subject === 'no template found'; // Note: this is what BE returns when no template is found

  return (
    <Dialog
      open={isOpen}
      title={t('flexMatch.createMessageForEmployee', {
        employeeName: selectedEmployee?.employeeName
      })}
      handleClose={handleClose}
      slots={{
        paper: StyledCustomDialogPaperSmall
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSendMessage)}>
          {!isPhoneType && (
            <StyledTextFieldContainer>
              <ControlledTextField
                control={control}
                label={t('flexMatch.subject')}
                name="subject"
              />
            </StyledTextFieldContainer>
          )}
          <ControlledRichTextEditor
            control={control}
            name="body"
            onSave={{ label: t('ship'), action: handleSendMessage }}
            textLimit={1000}
            rules={{
              validate: (html) =>
                (html &&
                  DOMPurify.sanitize(html, {
                    ALLOWED_TAGS: [],
                    ALLOWED_ATTR: []
                  }).trim().length > 0) ||
                t('validation.required')
            }}
          />
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default CommunicationDialog;
